import { Input } from 'src/components/Input';
import { SkillCard } from './SkillCard';
import { useCallback, useState } from 'react';
import { Label } from 'src/components/Label';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { processPhoneNumber } from 'src/utils/number';
import { Number } from 'src/interfaces/number.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import useAuthedRequest from 'src/hooks/useAuthedRequest';

export enum CommunicationType {
  PHONE = 'phone',
}

export type SwitchCommunicationConfig = {
  communication_types?: CommunicationType[];
  from_number?: string;
  switch_initial_message?: string;
};

export const validateSwitchCommunicationConfig = (
  config: SwitchCommunicationConfig | undefined,
): boolean => {
  if (!config || !config.communication_types) {
    return false;
  }
  return config.communication_types.length > 0 && !!config.from_number?.trim();
};

export function SwitchCommunicationSkill({
  config,
  onConfigChange,
}: {
  config: SwitchCommunicationConfig;
  onConfigChange?: (config: SwitchCommunicationConfig) => void;
}) {
  const { get } = useAuthedRequest();
  const [communicationTypes] = useState<CommunicationType[]>(
    config.communication_types ?? [CommunicationType.PHONE],
  );
  const [fromNumber, setFromNumber] = useState<string>(
    config.from_number ?? '',
  );
  const [switchInitialMessage, setSwitchInitialMessage] = useState<string>(
    config.switch_initial_message ?? '',
  );
  const [loading, setLoading] = useState(false);

  const searchCallerNumbers = useCallback(async (search: string) => {
    setLoading(true);
    const queryObject: QueryObject = {
      page: '1',
      size: '50',
      filters: JSON.stringify({
        number: search,
      }),
    };

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/numbers?${query.toString()}`);
    setLoading(false);

    return data.items.map((item: Number) => ({
      value: item.number,
      label: processPhoneNumber(item.number),
    }));
  }, []);

  return (
    <SkillCard description="Set the message the agent uses when switching communication channels.">
      <div className="mb-6">
        <Label required>Caller Number</Label>

        <AutoCompleteSelectBox
          defaultValue={
            fromNumber
              ? {
                  value: fromNumber,
                  label: processPhoneNumber(fromNumber),
                }
              : undefined
          }
          fetchOptions={searchCallerNumbers}
          onChange={(value) => {
            setFromNumber(value);
            onConfigChange?.({
              ...config,
              from_number: value,
              communication_types: communicationTypes,
            });
          }}
          variant="outlined"
          color="primary"
          size="medium"
          disabled={loading}
        />
      </div>
      <div className="mb-4">
        <Input
          label="Switch Communication Message"
          placeholder="Hey, I'm calling you as you requested. Want to continue where we left off?"
          value={switchInitialMessage}
          onChange={(e) => {
            setSwitchInitialMessage(e.target.value);
            onConfigChange?.({
              ...config,
              switch_initial_message: e.target.value,
              communication_types: communicationTypes,
            });
          }}
          disabled={loading}
        />
      </div>
    </SkillCard>
  );
}
